import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Mail, MapPin, Heart, Calendar, Send, ChevronDown, Sparkles, MessageCircle, Cookie, Shield } from "lucide-react";
import Impressum from "@/pages/Impressum";
import Datenschutz from "@/pages/Datenschutz";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: "easeOut" }
};

const staggerContainer = {
  initial: {},
  whileInView: { transition: { staggerChildren: 0.1 } }
};

// Image URLs from design guidelines with SEO alt texts
const images = {
  hero: {
    url: "https://customer-assets.emergentagent.com/job_eventdeko/artifacts/7mj6xcvx_tischschals-beige-hochzeit-tischdekoration-vellmar-dekorent.JPG",
    alt: "Elegante Hochzeitsdekoration mit Tischdeko in Vellmar - DekoRent Eventdekoration"
  },
  about: {
    url: "https://customer-assets.emergentagent.com/job_eventdeko/artifacts/gwn15lsa_hallo-ich-bins-karen-dekorent.png",
    alt: "Karen Tappe - Inhaberin DekoRent Vellmar - Persönliche Beratung für Eventdekoration"
  },
  products: [
    {
      id: 1,
      title: "Dekoschals mieten",
      description: "Fließende Dekoschals in zarten Farben für Hochzeiten und Feiern in Vellmar & Kassel",
      image: "https://customer-assets.emergentagent.com/job_eventdeko/artifacts/7mj6xcvx_tischschals-beige-hochzeit-tischdekoration-vellmar-dekorent.JPG",
      alt: "Dekoschals mieten für Hochzeit - Tischläufer Verleih Vellmar Kassel"
    },
    {
      id: 2,
      title: "Dekogläser leihen",
      description: "Elegante Dekogläser für Kerzen und Blumen - perfekt für Ihre Tischdekoration",
      image: "https://customer-assets.emergentagent.com/job_eventdeko/artifacts/gj1mo26e_dekoglaeser-tischdekoration-hochzeit-vellmar-dekorent.JPG",
      alt: "Dekogläser für Hochzeitsfeier leihen - Kerzenhalter Verleih Kassel Umgebung"
    },
    {
      id: 3,
      title: "Tischdekoration Hochzeit",
      description: "Komplette Tischdeko-Sets für unvergessliche Hochzeiten und Feiern in Nordhessen",
      image: "https://customer-assets.emergentagent.com/job_eventdeko/artifacts/49ixkqy0_tischsets-gold-hochzeit-tischdekoration-vellmar-dekorent.JPG",
      alt: "Tischdekoration Hochzeit Vellmar - Eventdeko Verleih Kassel"
    },
    {
      id: 4,
      title: "Candy Bar",
      description: "Süße Highlights für Ihre Feier – stilvolle Candy Bar Dekoration für Hochzeiten & Events",
      image: "https://customer-assets.emergentagent.com/job_eventdeko/artifacts/rg9ubp04_candybar-dekoration-hochzeit-vellmar-dekorent.JPG",
      alt: "Candy Bar Dekoration mieten - Sweet Table Hochzeit Vellmar Kassel"
    }
  ],
  gallery: [
    { url: "https://customer-assets.emergentagent.com/job_eventdeko/artifacts/ki2w6r6k_dekoglaeser-tiscdekoration-hochzeit-firma-impressionen.JPG", alt: "Tischdekoration mit Pampasgras - Hochzeitsdeko Vellmar" },
    { url: "https://customer-assets.emergentagent.com/job_eventdeko/artifacts/49ixkqy0_tischsets-gold-hochzeit-tischdekoration-vellmar-dekorent.JPG", alt: "Tischsets gold - Eventdekoration Kassel mieten" },
    { url: "https://customer-assets.emergentagent.com/job_eventdeko/artifacts/gj1mo26e_dekoglaeser-tischdekoration-hochzeit-vellmar-dekorent.JPG", alt: "Dekogläser mit Pampasgras - Deko Verleih Vellmar" },
    { url: "https://customer-assets.emergentagent.com/job_eventdeko/artifacts/fwllaz0c_dekoglaeser-tiscdekoration-hochzeit-firma-impressionen-2.JPG", alt: "Sektempfang Dekoration - Gläser mieten Hochzeit Kassel" },
    { url: "https://customer-assets.emergentagent.com/job_eventdeko/artifacts/ext5y36m_getraenkekarten-flasche-led-hochzeit-vellmar-dekorent.JPG", alt: "LED Getränkekarte Flasche - Dekoration für Feiern Vellmar" },
    { url: "https://customer-assets.emergentagent.com/job_eventdeko/artifacts/bf1qklb1_banner-tiscdekoration-hochzeit-firma-impressionen.JPG", alt: "Just Married Banner - Hochzeitsdekoration Vellmar" }
  ]
};

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#start", label: "Start" },
    { href: "#ueber-mich", label: "Über mich" },
    { href: "#dekoration", label: "Dekoration" },
    { href: "#galerie", label: "Galerie" },
    { href: "#kontakt", label: "Kontakt" }
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsOpen(false);
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-header shadow-sm" : "bg-transparent"
      }`}
      data-testid="navigation"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a 
            href="#start" 
            onClick={(e) => { e.preventDefault(); scrollToSection("#start"); }}
            className="flex items-center gap-2"
            data-testid="logo-link"
          >
            <span className="font-script text-3xl text-[#7C9082]">DekoRent</span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => { e.preventDefault(); scrollToSection(link.href); }}
                className="text-[#2C3329] hover:text-[#7C9082] transition-colors duration-300 font-medium"
                data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </a>
            ))}
            <Button 
              onClick={() => scrollToSection("#kontakt")}
              className="bg-[#7C9082] hover:bg-[#6A7D70] text-white rounded-full px-6 py-2 transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
              data-testid="nav-cta-button"
            >
              Anfragen
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 text-[#2C3329]"
            data-testid="mobile-menu-button"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden mobile-menu border-t border-stone-200"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 space-y-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => { e.preventDefault(); scrollToSection(link.href); }}
                  className="block text-[#2C3329] hover:text-[#7C9082] transition-colors py-2 font-medium"
                >
                  {link.label}
                </a>
              ))}
              <Button 
                onClick={() => scrollToSection("#kontakt")}
                className="w-full bg-[#7C9082] hover:bg-[#6A7D70] text-white rounded-full py-3"
              >
                Anfragen
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

// Hero Section - SEO optimiert
const HeroSection = () => {
  return (
    <section id="start" className="relative min-h-screen flex items-center pt-20" data-testid="hero-section">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src={images.hero.url}
          alt={images.hero.alt}
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#F9F7F2]/95 via-[#F9F7F2]/80 to-transparent"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <span className="inline-block text-[#D4AF37] font-medium tracking-widest uppercase text-sm mb-4">
              Eventdekoration Vellmar & Kassel
            </span>
            <h1 className="text-5xl md:text-7xl font-medium tracking-tight leading-tight text-[#2C3329] mb-6">
              Dekoration mieten <br />
              <span className="font-script text-[#7C9082]">für jeden Anlass</span>
            </h1>
            <p className="text-lg md:text-xl text-[#5C6658] leading-relaxed mb-8 max-w-lg">
              <strong>Hochzeitsdekoration, Dekoschals & Dekogläser</strong> zum Mieten in Vellmar, 
              Kassel und Umgebung. Verleihen Sie Ihrer Hochzeit, Ihrem Geburtstag oder Ihrer 
              privaten Feier den perfekten Rahmen – persönlich und herzlich beraten.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => document.querySelector("#kontakt")?.scrollIntoView({ behavior: "smooth" })}
                className="bg-[#7C9082] hover:bg-[#6A7D70] text-white rounded-full px-8 py-6 text-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
                data-testid="hero-cta-button"
              >
                <Sparkles className="mr-2 h-5 w-5" />
                Jetzt unverbindlich anfragen
              </Button>
              <Button 
                variant="outline"
                onClick={() => document.querySelector("#galerie")?.scrollIntoView({ behavior: "smooth" })}
                className="border-[#7C9082] text-[#7C9082] hover:bg-[#7C9082] hover:text-white rounded-full px-8 py-6 text-lg transition-all duration-300"
                data-testid="hero-gallery-button"
              >
                Inspiration entdecken
              </Button>
            </div>
            {/* Trust badges */}
            <div className="mt-8 flex flex-wrap gap-4 text-sm text-[#8C9688]">
              <span className="flex items-center gap-1">✓ Persönliche Beratung</span>
              <span className="flex items-center gap-1">✓ Preise auf Anfrage</span>
              <span className="flex items-center gap-1">✓ Vellmar & Umgebung</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="text-[#7C9082] cursor-pointer"
          onClick={() => document.querySelector("#ueber-mich")?.scrollIntoView({ behavior: "smooth" })}
          aria-label="Nach unten scrollen"
        >
          <ChevronDown size={32} />
        </motion.div>
      </motion.div>
    </section>
  );
};

// About Section - SEO optimiert
const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="ueber-mich" className="py-24 md:py-32 bg-white" ref={ref} data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative flex items-center justify-center"
          >
            <div className="relative">
              {/* Circular image container */}
              <div className="w-80 h-80 md:w-96 md:h-96 rounded-full overflow-hidden border-4 border-[#D4AF37] shadow-2xl">
                <img
                  src={images.about.url}
                  alt={images.about.alt}
                  className="w-full h-full object-cover object-center scale-110"
                  loading="lazy"
                />
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-[#E5E0D8] rounded-full -z-10"></div>
              <div className="absolute -top-4 -left-4 w-16 h-16 bg-[#7C9082]/20 rounded-full -z-10"></div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <span className="inline-block text-[#D4AF37] font-medium tracking-widest uppercase text-sm mb-4">
              Ihre Ansprechpartnerin in Vellmar
            </span>
            <h2 className="text-4xl md:text-5xl font-medium tracking-tight text-[#2C3329] mb-6">
              Hallo, ich bin <span className="font-script text-[#7C9082]">Karen Tappe</span>
            </h2>
            <div className="space-y-4 text-[#5C6658] text-lg leading-relaxed">
              <p>
                Dekoration ist meine Leidenschaft! Ich liebe es, wenn Menschen zusammenkommen, 
                lachen und besondere Momente teilen. Mit der richtigen <strong>Eventdekoration</strong> werden 
                diese Momente unvergesslich.
              </p>
              <p>
                Mit <strong className="text-[#2C3329]">DekoRent in Vellmar</strong> helfe ich Ihnen, 
                Ihre Feier in einen wunderschönen Ort zu verwandeln – ganz ohne großen 
                Aufwand. Ob <strong>Hochzeitsdekoration in Kassel</strong>, Geburtstag, Taufe oder Jubiläum: 
                Bei mir finden Sie <strong>Dekoschals, Dekogläser und Tischdekoration</strong> zum Mieten.
              </p>
              <p>
                Ich berate Sie persönlich und unkompliziert in <strong>Vellmar, Kassel und Umgebung</strong>. 
                Freuen Sie sich auf liebevoll gepflegte Dekoartikel und individuelle Beratung – 
                <strong> Preise auf Anfrage</strong>!
              </p>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 text-[#7C9082]">
                <MapPin size={20} />
                <span className="font-medium">Vellmar, Kassel & Nordhessen</span>
              </div>
              <div className="w-px h-6 bg-[#E5E0D8] hidden sm:block"></div>
              <div className="flex items-center gap-2 text-[#7C9082]">
                <Heart size={20} />
                <span className="font-medium">Persönliche Beratung</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Products Section - SEO optimiert
const ProductsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="dekoration" className="py-24 md:py-32 bg-[#F9F7F2]" ref={ref} data-testid="products-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="inline-block text-[#D4AF37] font-medium tracking-widest uppercase text-sm mb-4">
            Deko Verleih Vellmar
          </span>
          <h2 className="text-4xl md:text-5xl font-medium tracking-tight text-[#2C3329] mb-6">
            Eventdekoration zum <span className="font-script text-[#7C9082]">Mieten</span>
          </h2>
          <p className="text-lg text-[#5C6658] max-w-2xl mx-auto">
            Hochwertige <strong>Hochzeitsdekoration, Dekoschals und Dekogläser</strong> zum Leihen 
            in Vellmar, Kassel und Umgebung. Perfekt für Hochzeiten, Geburtstage und private Feiern – 
            <strong> Preise auf Anfrage</strong>.
          </p>
        </motion.div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {images.products.map((product, index) => (
            <motion.article
              key={product.id}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="group relative overflow-hidden rounded-2xl bg-white shadow-sm hover:shadow-xl transition-all duration-500"
              data-testid={`product-card-${product.id}`}
            >
              <div className="overflow-hidden">
                <img
                  src={product.image}
                  alt={product.alt}
                  className="w-full h-72 object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-medium text-[#2C3329] mb-2">{product.title}</h3>
                <p className="text-[#5C6658]">{product.description}</p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-sm text-[#8C9688] uppercase tracking-wide">Preise auf Anfrage</span>
                  <span className="inline-flex items-center gap-1 text-[#7C9082] font-medium group-hover:gap-2 transition-all">
                    Anfragen <ChevronDown className="rotate-[-90deg] h-4 w-4" />
                  </span>
                </div>
              </div>
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#2C3329]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
            </motion.article>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-center mt-12"
        >
          <Button 
            onClick={() => document.querySelector("#kontakt")?.scrollIntoView({ behavior: "smooth" })}
            className="bg-[#7C9082] hover:bg-[#6A7D70] text-white rounded-full px-8 py-6 text-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
            data-testid="products-cta-button"
          >
            Dekoration für Ihre Feier sichern
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

// Gallery Section - SEO optimiert
const GallerySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="galerie" className="py-24 md:py-32 bg-white" ref={ref} data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="inline-block text-[#D4AF37] font-medium tracking-widest uppercase text-sm mb-4">
            Hochzeitsdeko & Eventdekoration
          </span>
          <h2 className="text-4xl md:text-5xl font-medium tracking-tight text-[#2C3329] mb-6">
            Inspiration für <span className="font-script text-[#7C9082]">Ihre Feier</span>
          </h2>
          <p className="text-lg text-[#5C6658] max-w-2xl mx-auto">
            Lassen Sie sich inspirieren! Entdecken Sie, wie unsere <strong>Hochzeitsdekoration 
            und Eventdeko</strong> Feiern in Vellmar, Kassel und Umgebung unvergesslich macht.
          </p>
        </motion.div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {images.gallery.map((image, index) => (
            <motion.figure
              key={index}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className={`gallery-item relative overflow-hidden rounded-xl ${
                index === 0 ? "md:col-span-2 md:row-span-2" : ""
              }`}
              data-testid={`gallery-item-${index}`}
            >
              <img
                src={image.url}
                alt={image.alt}
                className={`w-full object-cover ${
                  index === 0 ? "h-full min-h-[400px]" : "h-64"
                }`}
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2C3329]/40 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500 flex items-end p-6">
                <figcaption className="text-white font-medium">{image.alt.split(' - ')[0]}</figcaption>
              </div>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section - SEO optimiert
const ContactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    occasion: "",
    event_date: "",
    location: "",
    message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        toast.success("Anfrage gesendet!", {
          description: response.data.message
        });
        setFormData({
          name: "",
          email: "",
          phone: "",
          occasion: "",
          event_date: "",
          location: "",
          message: ""
        });
      }
    } catch (error) {
      toast.error("Fehler beim Senden", {
        description: "Bitte versuchen Sie es später erneut oder kontaktieren Sie uns direkt."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section id="kontakt" className="py-24 md:py-32 bg-[#F9F7F2]" ref={ref} data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block text-[#D4AF37] font-medium tracking-widest uppercase text-sm mb-4">
              Dekoration anfragen in Vellmar
            </span>
            <h2 className="text-4xl md:text-5xl font-medium tracking-tight text-[#2C3329] mb-6">
              Jetzt unverbindlich <span className="font-script text-[#7C9082]">anfragen</span>
            </h2>
            <p className="text-lg text-[#5C6658] leading-relaxed mb-8">
              Sie planen eine <strong>Hochzeit in Kassel</strong>, einen Geburtstag oder eine private Feier? 
              Fragen Sie jetzt Ihre <strong>Eventdekoration</strong> an – ich berate Sie persönlich 
              und erstelle Ihnen ein individuelles Angebot. <strong>Preise auf Anfrage</strong>.
            </p>

            {/* Contact Details */}
            <address className="space-y-6 not-italic">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#7C9082]/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="h-5 w-5 text-[#7C9082]" />
                </div>
                <div>
                  <h4 className="font-medium text-[#2C3329] mb-1">Telefon</h4>
                  <a href="tel:017634594053" className="text-[#5C6658] hover:text-[#7C9082] transition-colors">
                    0176 34594053
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#7C9082]/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="h-5 w-5 text-[#7C9082]" />
                </div>
                <div>
                  <h4 className="font-medium text-[#2C3329] mb-1">E-Mail</h4>
                  <a href="mailto:info@dekorent-vellmar.de" className="text-[#5C6658] hover:text-[#7C9082] transition-colors">
                    info@dekorent-vellmar.de
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#7C9082]/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-5 w-5 text-[#7C9082]" />
                </div>
                <div>
                  <h4 className="font-medium text-[#2C3329] mb-1">Abholung</h4>
                  <p className="text-[#5C6658]">34246 Vellmar</p>
                </div>
              </div>
            </address>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 shadow-sm" data-testid="contact-form">
              <div className="space-y-6">
                {/* Name */}
                <div>
                  <Label htmlFor="name" className="text-[#2C3329] font-medium">Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-2 bg-[#F9F7F2] border-[#E5E0D8] focus:border-[#7C9082] focus:ring-[#7C9082]/20 rounded-lg"
                    placeholder="Ihr Name"
                    data-testid="input-name"
                  />
                </div>

                {/* Email & Phone */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email" className="text-[#2C3329] font-medium">E-Mail *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="mt-2 bg-[#F9F7F2] border-[#E5E0D8] focus:border-[#7C9082] focus:ring-[#7C9082]/20 rounded-lg"
                      placeholder="ihre@email.de"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-[#2C3329] font-medium">Telefon (optional)</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="mt-2 bg-[#F9F7F2] border-[#E5E0D8] focus:border-[#7C9082] focus:ring-[#7C9082]/20 rounded-lg"
                      placeholder="0123 456789"
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                {/* Occasion */}
                <div>
                  <Label htmlFor="occasion" className="text-[#2C3329] font-medium">Anlass *</Label>
                  <Select 
                    value={formData.occasion} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, occasion: value }))}
                    required
                  >
                    <SelectTrigger 
                      className="mt-2 bg-[#F9F7F2] border-[#E5E0D8] focus:border-[#7C9082] focus:ring-[#7C9082]/20 rounded-lg"
                      data-testid="select-occasion"
                    >
                      <SelectValue placeholder="Bitte wählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hochzeit">Hochzeit</SelectItem>
                      <SelectItem value="geburtstag">Geburtstag</SelectItem>
                      <SelectItem value="taufe">Taufe</SelectItem>
                      <SelectItem value="jubilaeum">Jubiläum</SelectItem>
                      <SelectItem value="sonstiges">Sonstiges</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Date & Location */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="event_date" className="text-[#2C3329] font-medium">Datum / Zeitraum *</Label>
                    <Input
                      id="event_date"
                      name="event_date"
                      type="text"
                      required
                      value={formData.event_date}
                      onChange={handleChange}
                      className="mt-2 bg-[#F9F7F2] border-[#E5E0D8] focus:border-[#7C9082] focus:ring-[#7C9082]/20 rounded-lg"
                      placeholder="z.B. 15.06.2025"
                      data-testid="input-date"
                    />
                  </div>
                  <div>
                    <Label htmlFor="location" className="text-[#2C3329] font-medium">Ort der Feier</Label>
                    <Select 
                      value={formData.location} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, location: value }))}
                    >
                      <SelectTrigger 
                        className="mt-2 bg-[#F9F7F2] border-[#E5E0D8] focus:border-[#7C9082] focus:ring-[#7C9082]/20 rounded-lg"
                        data-testid="select-location"
                      >
                        <SelectValue placeholder="Bitte wählen..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="vellmar">Vellmar</SelectItem>
                        <SelectItem value="kassel">Kassel</SelectItem>
                        <SelectItem value="umgebung">Umgebung</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <Label htmlFor="message" className="text-[#2C3329] font-medium">Ihre Nachricht *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="mt-2 bg-[#F9F7F2] border-[#E5E0D8] focus:border-[#7C9082] focus:ring-[#7C9082]/20 rounded-lg resize-none"
                    placeholder="Erzählen Sie mir von Ihrer Feier und welche Dekoration Sie sich vorstellen..."
                    data-testid="input-message"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#7C9082] hover:bg-[#6A7D70] text-white rounded-full py-6 text-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-button"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Wird gesendet...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Send className="h-5 w-5" />
                      Anfrage senden
                    </span>
                  )}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer - SEO optimiert
const Footer = () => {
  return (
    <footer className="py-12 bg-[#2C3329] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Logo */}
          <div className="flex flex-col items-center md:items-start">
            <span className="font-script text-3xl text-[#D4AF37]">DekoRent</span>
            <span className="text-xs text-[#8C9688] mt-1">Eventdekoration Vellmar</span>
          </div>

          {/* Made with love */}
          <div className="flex items-center gap-2 text-[#8C9688]">
            <span>Made with</span>
            <Heart className="h-4 w-4 text-[#D4AF37] fill-[#D4AF37]" />
            <span>in Vellmar, Hessen</span>
          </div>

          {/* Copyright */}
          <div className="text-[#8C9688] text-sm">
            © {new Date().getFullYear()} DekoRent - Karen Tappe
          </div>
        </div>

        {/* SEO Keywords Footer */}
        <div className="mt-6 text-center text-xs text-[#5C6658]">
          Dekoration mieten Vellmar | Hochzeitsdeko Kassel | Eventdekoration Nordhessen
        </div>

        {/* Links */}
        <nav className="mt-6 pt-6 border-t border-[#5C6658]/30 flex flex-col md:flex-row items-center justify-center gap-6 text-sm text-[#8C9688]">
          <Link to="/impressum" className="hover:text-white transition-colors" data-testid="footer-impressum-link">Impressum</Link>
          <span className="hidden md:inline">|</span>
          <Link to="/datenschutz" className="hover:text-white transition-colors" data-testid="footer-datenschutz-link">Datenschutz</Link>
          <span className="hidden md:inline">|</span>
          <a href="tel:017634594053" className="hover:text-white transition-colors">0176 34594053</a>
          <span className="hidden md:inline">|</span>
          <a href="mailto:info@dekorent-vellmar.de" className="hover:text-white transition-colors">info@dekorent-vellmar.de</a>
        </nav>
      </div>
    </footer>
  );
};

// WhatsApp Floating Button
const WhatsAppButton = () => {
  const phoneNumber = "4917634594053"; // German format without + and spaces
  const message = "Hallo! Ich interessiere mich für Ihre Dekorationsartikel und hätte gerne mehr Informationen.";
  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;

  return (
    <motion.a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-40 flex items-center gap-3 bg-[#25D366] hover:bg-[#20BD5A] text-white px-5 py-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group"
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: 1, duration: 0.5, type: "spring" }}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      data-testid="whatsapp-button"
    >
      <svg 
        viewBox="0 0 24 24" 
        className="w-6 h-6 fill-current"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
      <span className="hidden sm:inline font-medium">WhatsApp</span>
    </motion.a>
  );
};

// Cookie Banner Component
const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem("dekorent_cookie_consent");
    if (!cookieConsent) {
      // Show banner after a short delay
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem("dekorent_cookie_consent", JSON.stringify({
      essential: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
    toast.success("Cookie-Einstellungen gespeichert");
  };

  const handleAcceptEssential = () => {
    localStorage.setItem("dekorent_cookie_consent", JSON.stringify({
      essential: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
    toast.success("Nur notwendige Cookies akzeptiert");
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ duration: 0.5, type: "spring" }}
        className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
        data-testid="cookie-banner"
      >
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-[#E5E0D8] overflow-hidden">
          {/* Main Banner */}
          <div className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-[#7C9082]/10 flex items-center justify-center flex-shrink-0">
                <Cookie className="h-6 w-6 text-[#7C9082]" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-[#2C3329] mb-2">
                  Wir nutzen Cookies 🍪
                </h3>
                <p className="text-[#5C6658] text-sm leading-relaxed">
                  Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten. 
                  Sie können wählen, welche Cookies Sie zulassen möchten.
                </p>
                
                {/* Details Toggle */}
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="text-[#7C9082] text-sm font-medium mt-2 hover:underline flex items-center gap-1"
                  data-testid="cookie-details-toggle"
                >
                  {showDetails ? "Details ausblenden" : "Mehr erfahren"}
                  <ChevronDown className={`h-4 w-4 transition-transform ${showDetails ? "rotate-180" : ""}`} />
                </button>
              </div>
            </div>

            {/* Details Section */}
            <AnimatePresence>
              {showDetails && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-4 pt-4 border-t border-[#E5E0D8]"
                >
                  <div className="grid gap-4 md:grid-cols-3">
                    {/* Essential Cookies */}
                    <div className="p-4 bg-[#F9F7F2] rounded-xl">
                      <div className="flex items-center gap-2 mb-2">
                        <Shield className="h-5 w-5 text-[#7C9082]" />
                        <h4 className="font-medium text-[#2C3329]">Notwendig</h4>
                      </div>
                      <p className="text-xs text-[#5C6658]">
                        Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.
                      </p>
                      <span className="inline-block mt-2 text-xs bg-[#7C9082] text-white px-2 py-1 rounded-full">
                        Immer aktiv
                      </span>
                    </div>

                    {/* Analytics Cookies */}
                    <div className="p-4 bg-[#F9F7F2] rounded-xl">
                      <div className="flex items-center gap-2 mb-2">
                        <svg className="h-5 w-5 text-[#7C9082]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        <h4 className="font-medium text-[#2C3329]">Analyse</h4>
                      </div>
                      <p className="text-xs text-[#5C6658]">
                        Helfen uns zu verstehen, wie Besucher unsere Website nutzen, um sie zu verbessern.
                      </p>
                      <span className="inline-block mt-2 text-xs bg-[#E5E0D8] text-[#5C6658] px-2 py-1 rounded-full">
                        Optional
                      </span>
                    </div>

                    {/* Marketing Cookies */}
                    <div className="p-4 bg-[#F9F7F2] rounded-xl">
                      <div className="flex items-center gap-2 mb-2">
                        <svg className="h-5 w-5 text-[#7C9082]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                        </svg>
                        <h4 className="font-medium text-[#2C3329]">Marketing</h4>
                      </div>
                      <p className="text-xs text-[#5C6658]">
                        Werden verwendet, um Ihnen relevante Werbung anzuzeigen.
                      </p>
                      <span className="inline-block mt-2 text-xs bg-[#E5E0D8] text-[#5C6658] px-2 py-1 rounded-full">
                        Optional
                      </span>
                    </div>
                  </div>

                  <p className="text-xs text-[#8C9688] mt-4">
                    Weitere Informationen finden Sie in unserer{" "}
                    <Link to="/datenschutz" className="text-[#7C9082] hover:underline">
                      Datenschutzerklärung
                    </Link>
                    .
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <Button
                onClick={handleAcceptEssential}
                variant="outline"
                className="flex-1 border-[#7C9082] text-[#7C9082] hover:bg-[#7C9082] hover:text-white rounded-full py-3 transition-all duration-300"
                data-testid="cookie-accept-essential"
              >
                Nur notwendige
              </Button>
              <Button
                onClick={handleAcceptAll}
                className="flex-1 bg-[#7C9082] hover:bg-[#6A7D70] text-white rounded-full py-3 transition-all duration-300 shadow-lg hover:shadow-xl"
                data-testid="cookie-accept-all"
              >
                Alle akzeptieren
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// Main Home Page
const Home = () => {
  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="home-page">
      <Navigation />
      <HeroSection />
      <AboutSection />
      <ProductsSection />
      <GallerySection />
      <ContactSection />
      <Footer />
      <WhatsAppButton />
      <CookieBanner />
      <Toaster position="top-center" richColors />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/datenschutz" element={<Datenschutz />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
